import { useAuth } from '#globalContexts/AuthProvider';

/**
 * Shared hook for capability-based access control.
 *
 * Capabilities are read synchronously from the auth context (derived from the JWT), so the
 * result is available on the first render once auth has finished loading — there is no separate
 * async fetch and therefore no intermediate loading flicker beyond `authLoading`.
 *
 * @param {string|string[]|undefined} required - capability or list of capabilities the user must have
 * @returns {{ hasCapability: boolean, isLoading: boolean }}
 */
export function useCapabilityCheck(required) {
	const { user, capabilities, loading: authLoading } = useAuth();

	// No requirement → always allowed (e.g. routes that only need authentication).
	if (!required) {
		return { hasCapability: true, isLoading: authLoading };
	}

	if (!user) {
		return { hasCapability: false, isLoading: authLoading };
	}

	const needed = Array.isArray(required) ? required : [required];
	const hasCapability = needed.every((c) => capabilities.includes(c));

	return { hasCapability, isLoading: authLoading };
}
